// AI-composer content-reference machinery (2026-09-02 per Nika: "add + icon
// where you select different content - like ticket, member, note etc. it
// could be also mentioned by @ ++ # and etc just like we have in chat").
//
// The trigger grammar and the SDK fetchers deliberately MIRROR the chat
// composer's (packages/chat/functions/messageBody.js TRIGGERS +
// packages/chat/components/Composer.js _fetchRef) — one mental model for
// mentions across chat and the AI prompt. They are re-declared here, under
// DISTINCT exported names, because (a) canvas cannot legally import from the
// chat package (cross-app import boundary, eslint.config.mjs), and (b)
// re-exporting chat's names from shared would collide in the merged
// function registry once the chat context folds into the shell (Rule 63 —
// duplicate names shadow silently). If the trigger grammar ever changes,
// change BOTH tables in the same commit — each file's header names the
// other.
//
// Person/channel options resolve SYNCHRONOUSLY from state the shell already
// holds (root.people — the member directory loadPrivateData maintains;
// root.channels when the chat context has booted). Ticket/event/reference
// options fetch on demand through the SDK — same services, filters and
// 60s cache the chat composer uses.

import { getSDK } from './sdk.js'

export const AI_MENTION_TRIGGERS = [
  { prefix: '@@', type: 'event' },
  { prefix: '##', type: 'ticket' },
  { prefix: '@', type: 'person' },
  { prefix: '#', type: 'channel' },
  { prefix: '+', type: 'reference' }
]

// Returns { type, query, start, end } or null — same contract as chat's
// detectReferenceTrigger, over a PLAIN-TEXT body + caret (the AI composer is
// a <textarea>, not a contenteditable, so start/end index the string).
export const detectMentionRefTrigger = (body, caret) => {
  if (caret == null || !body) return null
  const upToCaret = body.slice(0, caret)
  for (const { prefix, type } of AI_MENTION_TRIGGERS) {
    const idx = upToCaret.lastIndexOf(prefix)
    if (idx < 0) continue
    if (idx > 0 && !/\s/.test(body[idx - 1])) continue
    const after = upToCaret.slice(idx + prefix.length)
    if (!/^[a-zA-Z0-9._\- ]*$/.test(after)) continue
    return { type, query: after, start: idx, end: caret }
  }
  return null
}

// The text a chosen option becomes — mirrors chat's mentionInsertText.
export const mentionRefInsertText = (type, option) => {
  if (!option) return ''
  if (type === 'person') {
    const handle = (option.email || '').split('@')[0] || option.name
    return handle ? `@${handle} ` : ''
  }
  if (type === 'channel') return option.name ? `#${option.name} ` : ''
  if (type === 'ticket') {
    const ref = option.number || option.id
    return ref ? `##${ref} ` : ''
  }
  if (type === 'event') return option.title ? `@@${option.title} ` : ''
  if (type === 'reference') return option.title ? `+${option.title} ` : ''
  return ''
}

const _normalizeList = (res) => {
  if (Array.isArray(res)) return res
  if (res && Array.isArray(res.data)) return res.data
  return []
}

// 60s soft cache, per type — same shape/TTL as chat's _refCache so the two
// composers put identical load on the services.
const _cache = {}
const _cached = async (key, fill) => {
  if (!_cache[key]) {
    _cache[key] = await fill()
    setTimeout(() => {
      _cache[key] = null
    }, 60000)
  }
  return _cache[key]
}

// Synchronous option sources — resolved from root state, no fetch.
export const mentionRefSyncOptions = (type, query, root) => {
  const q = (query || '').trim().toLowerCase()
  if (type === 'person') {
    const people = Array.isArray(root && root.people) ? root.people : []
    return people
      .filter(
        (p) =>
          !q ||
          (p.email && p.email.toLowerCase().split('@')[0].includes(q)) ||
          (p.name && p.name.toLowerCase().includes(q))
      )
      .slice(0, 6)
  }
  if (type === 'channel') {
    return (root && root.channels ? root.channels : [])
      .filter((c) => c.kind === 'channel' && (!q || (c.name && c.name.toLowerCase().includes(q))))
      .slice(0, 6)
  }
  return null
}

// Async option sources — SDK-backed, mirrors chat's _fetchRef branch by
// branch (tickets service list; workspaceProject.calendar list; docs.notes +
// docs.kbArticles).
export const fetchMentionRefOptions = async (type, query) => {
  const q = (query || '').trim().toLowerCase()
  if (type === 'ticket') {
    const rows = await _cached('tickets', async () => {
      const tSvc = await getSDK().getService('tickets')
      const res = await tSvc.list(
        { deleted: false },
        { limit: 50, sortBy: 'updatedAt', sortDir: 'desc' }
      )
      return _normalizeList(res)
    })
    return rows
      .filter(
        (t) =>
          !q ||
          (t.title && t.title.toLowerCase().includes(q)) ||
          (t.number && String(t.number).includes(q))
      )
      .slice(0, 6)
  }
  if (type === 'event') {
    const rows = await _cached('events', async () => {
      const res = await getSDK().execute('workspaceProject.calendar', 'list', {
        filter: {},
        options: {
          columns: 'id, title, date, start_time, end_time',
          order: { date: 'desc' },
          limit: 50
        }
      })
      return _normalizeList(res)
    })
    return rows.filter((ev) => !q || (ev.title && ev.title.toLowerCase().includes(q))).slice(0, 6)
  }
  if (type === 'reference') {
    const notes = await _cached('notes', async () => {
      const res = await getSDK().execute('docs.notes', 'list', { limit: 30 })
      return _normalizeList(res).map((n) => ({
        ...n,
        id: n.docId || n._id || n.id,
        _refType: 'note'
      }))
    })
    const kb = await _cached('kb', async () => {
      const res = await getSDK().execute('docs.kbArticles', 'list', { limit: 30 })
      return _normalizeList(res).map((a) => ({
        ...a,
        id: a.docId || a._id || a.id,
        _refType: 'knowledge base'
      }))
    })
    return [...notes, ...kb]
      .filter((d) => !q || (d.title && d.title.toLowerCase().includes(q)))
      .slice(0, 6)
  }
  return []
}

// One row label + caption per option, so the dropdown renderer never
// branches on type.
export const mentionRefRowLabel = (type, option) => {
  if (!option) return { label: '', caption: '' }
  if (type === 'person') {
    return {
      label: option.name || option.email || '',
      caption: option.email ? '@' + option.email.split('@')[0] : ''
    }
  }
  if (type === 'channel') return { label: '#' + (option.name || ''), caption: 'Channel' }
  if (type === 'ticket') {
    return {
      label: option.title || String(option.number || option.id || ''),
      caption: option.number ? `##${option.number}` : 'Ticket'
    }
  }
  if (type === 'event') return { label: option.title || '', caption: option.date || 'Event' }
  if (type === 'reference') {
    return { label: option.title || '', caption: option._refType || 'Reference' }
  }
  return { label: option.title || option.name || '', caption: '' }
}
